import type { GalleryCardType, GalleryDetailType } from "@volgo/core/contract";
import { Link, useLocation } from "react-router";
import { ackInput, useAcks } from "../policy/ack";
import { EMPTY_QUERY, splitTag, toSearchParams } from "../search/query";
import { useSession } from "../session";
import { Cover, GalleryCard } from "../ui/GalleryCard";
import { useLoad } from "../ui/useLoad";

const PREVIEW_PAGES = 8;
const SAME_GROUP = 12;

/**
 * The first pages of the completed download, each opening the reader at that page (46 Spec 3.3, 3.4).
 * The caller draws it only for a gallery with a completed download that may be shown (not blocked, a warning
 * confirmed); the server checks again, and any refusal or an empty answer leaves no section at all.
 */
export function PagePreview({ gid }: { gid: number }) {
  const { client } = useSession();
  const acks = useAcks();
  const pages = useLoad(
    () => client.call("reader.pages", { params: { gid }, query: ackInput(acks, gid) }),
    `preview:${gid}`,
  );
  if (pages.status !== "ready" || pages.data.pages.length === 0) return null;
  const shown = pages.data.pages.slice(0, PREVIEW_PAGES);
  return (
    <section className="dw-sec" aria-label="미리보기">
      <div className="sh">
        <h2>미리보기</h2>
        <span className="sub">
          {shown.length} / {pages.data.pageCount}쪽
        </span>
      </div>
      <div className="dw-pages">
        {shown.map((page) => (
          <Link
            key={page.index}
            className="cv"
            to={`/read/${gid}?p=${page.index}`}
            state={{ inApp: true }}
            aria-label={`${page.index}쪽부터 읽기`}
          >
            <Cover src={page.url} />
          </Link>
        ))}
      </div>
    </section>
  );
}

/** The first `group:` tag, else the first `artist:` tag: what "the same group" is searched by. */
export function sameGroupTag(tags: readonly string[]): string | null {
  return tags.find((t) => t.startsWith("group:")) ?? tags.find((t) => t.startsWith("artist:")) ?? null;
}

/**
 * Other galleries with the same group (or artist) tag, as the list's own cards in one row.
 * A card opens in the same window over the same list; going back returns to this gallery.
 */
export function SameGroup({ gallery }: { gallery: GalleryCardType | GalleryDetailType }) {
  const tag = sameGroupTag(gallery.tags);
  return tag === null ? null : <SameGroupRow gid={gallery.gid} tag={tag} />;
}

function SameGroupRow({ gid, tag }: { gid: number; tag: string }) {
  const { client } = useSession();
  const location = useLocation();
  const list = useLoad(
    () => client.call("catalog.list", { query: { tag: [tag], limit: SAME_GROUP + 1 } }),
    tag,
  );
  const items = (list.data?.items ?? []).filter((card) => card.gid !== gid).slice(0, SAME_GROUP);
  if (items.length === 0) return null;
  const { namespace, name } = splitTag(tag);
  const title = `${namespace === "group" ? "같은 그룹" : "같은 작가"}: ${name}`;
  const from = location.state as { background?: unknown; depth?: number; listed?: number } | null;
  return (
    <section className="dw-sec" aria-label={title}>
      <div className="sh">
        <h2>{title}</h2>
        <Link
          className="sh-more"
          to={{ pathname: "/", search: toSearchParams({ ...EMPTY_QUERY, tags: [tag] }).toString() }}
        >
          모두
        </Link>
      </div>
      <div className="dw-rail">
        {items.map((card) => (
          <GalleryCard
            key={card.gid}
            card={card}
            to={`/g/${card.gid}`}
            // the list under the window stays; `depth` is how many windows closing has to step back over,
            // `listed` the list's gallery whose card gets focus back
            state={{
              background: from?.background,
              depth: (from?.depth ?? 0) + 1,
              listed: from?.listed ?? gid,
            }}
          />
        ))}
      </div>
    </section>
  );
}
